import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
  Animated as RNAnimated,
  Easing,
} from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { ChatStackParamList } from "../types";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  cancelAnimation,
  withRepeat,
  withTiming,
  withSequence,
  interpolateColor,
} from "react-native-reanimated";
import {
  Layers,
  BookOpen,
  GitFork,
  FlaskConical,
  Coins,
  Stethoscope,
  Pill,
  Leaf,
  Heart,
  Bookmark,
  Sparkles,
  Library,
  Plane,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { getBlocksByTag, getBlocksByKind } from "@louise/content";
import type { ContentBlock, ArticleBody, ContentTag } from "@louise/content";
import { ChatwootSheet } from "@louise/chatwoot";
import { useReadArticles, useFavoriteArticles } from "@louise/favorites";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
} from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CARD_RADIUS,
  FEATURE_CARD_RADIUS,
  Tag,
  cardSurface,
  SectionHeader,
  MoonFilterChip,
  EmptyState,
  DialogIcon,
  CompactActionButton,
} from "@louise/moon-ui";
import savedArticlesIllustration from "../../assets/images/you-icons/learn.png";
import {
  SCREEN_PADDING_TOP,
  HEADER_INSET,
  semanticColors,
} from "@louise/moon-ui/theme/tokens";
import ScreenHeader from "../components/ScreenHeader";
import { AiAssistantCard } from "../components/AiAssistantCard";
import ArticleSectionCard from "../components/ArticleSectionCard";
import type { Article } from "../components/ArticleSectionCard";
import { SECTION_TAGS } from "../data/articleSectionTags";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { getRecommendedArticleIds } from "@src/packages/persona/learnRecommendations";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";

/* ─── Map ContentBlock → Article ─── */

// Maps a content tag to a key under `learn.article_tags.*`. Used as the
// fallback tag-pill label when an article's body.introduction is too long
// or sentence-shaped to use directly. Only the first matching tag wins.
const TAG_I18N_KEYS: Partial<Record<ContentTag, string>> = {
  "moon-guides": "guides",
  "moon-decisions": "decisions",
  "moon-science": "science",
  "moon-costs": "costs",
  endo: "endometriosis",
  pcos: "pcos",
  thyroid: "thyroid",
  "clinic-visit": "clinic",
  "during-cycle": "treatment",
  "pre-cycle": "preparation",
  "egg-freezing": "egg_freezing",
  lifestyle: "lifestyle",
  optimization: "optimization",
  emotional: "emotional_support",
  testing: "testing",
  practical: "practical",
  travel: "travel",
  "both-partners": "partners",
  his: "male_fertility",
  her: "female_fertility",
  clinic: "clinic",
};

function deriveTag(
  block: ContentBlock<"article">,
  t: TranslationFunction,
): string {
  const body = block.body as ArticleBody;
  // Moon articles store a short label in body.introduction
  if (
    body.introduction &&
    body.introduction.length <= 30 &&
    !body.introduction.includes(".")
  ) {
    return body.introduction;
  }
  // For shared articles, derive a readable tag from content tags
  for (const tag of block.tags) {
    const key = TAG_I18N_KEYS[tag];
    if (key) return t(`learn.article_tags.${key}`);
  }
  return "";
}

function deriveTeaser(block: ContentBlock<"article">): string {
  // Use subtitle if it's short enough for a teaser
  if (block.subtitle && block.subtitle.length <= 80) {
    return block.subtitle;
  }
  // Truncate long subtitles
  if (block.subtitle) {
    return block.subtitle.slice(0, 77) + "...";
  }
  // Fall back to first section content
  const body = block.body as ArticleBody;
  const first = body.sections[0]?.content;
  if (first) {
    return first.length <= 80 ? first : first.slice(0, 77) + "...";
  }
  return "";
}

function blockToArticle(
  block: ContentBlock<"article">,
  t: TranslationFunction,
): Article {
  const body = block.body as ArticleBody;
  return {
    id: block.id,
    title: block.title,
    time: block.readTimeMinutes
      ? t("learn.read_time_minutes", {
          params: { minutes: String(block.readTimeMinutes) },
        })
      : "",
    tag: deriveTag(block, t),
    teaser: deriveTeaser(block),
    body,
    tags: block.tags,
  };
}

/* ─── Collect all articles ─── */
// Locale-aware: re-derive whenever the active locale changes so translated
// titles/teasers/bodies surface immediately.

// Reads the locale-aware content registry. The `_locale` parameter exists
// solely so callers can include it in a useMemo dep array — the registry's
// active locale is mutated externally, so we need an explicit signal that
// the cache should re-derive on locale change.
function collectArticles(_locale: string, t: TranslationFunction) {
  void _locale;
  const moonBlocks = getBlocksByTag("moon").filter(
    (b): b is ContentBlock<"article"> => b.kind === "article",
  );

  const moonArticles = moonBlocks
    .filter((b) => !b.tags.includes("moon-spotlight"))
    .map((b) => blockToArticle(b, t));

  const spotlightBlock = moonBlocks.find((b) =>
    b.tags.includes("moon-spotlight"),
  );
  const spotlightArticle: Article | null = spotlightBlock
    ? blockToArticle(spotlightBlock, t)
    : (moonArticles[0] ?? null);

  const sharedBlocks = getBlocksByKind("article").filter(
    (b) => !b.tags.includes("moon" as ContentTag),
  ) as ContentBlock<"article">[];
  const sharedArticles = sharedBlocks.map((b) => blockToArticle(b, t));

  return {
    moonArticles,
    sharedArticles,
    spotlightArticle,
    allArticles: [...moonArticles, ...sharedArticles],
  };
}

const RECOMMENDED_TEXTURES = [
  require("../../assets/images/dark-copper-texture.png"),
  require("../../assets/images/copper-texture-2.png"),
  require("../../assets/images/copper-texture-3.png"),
];

/* ─── Helpers ─── */

function hasAnyTag(article: Article, tags: ContentTag[]): boolean {
  return article.tags.some((t) => tags.includes(t as ContentTag));
}

/* ─── Sections ─── */

type ArticleSection = {
  id: string;
  articles: Article[];
  showProgress?: boolean;
  accentColor: string;
};

function buildArticleSections(
  moonArticles: Article[],
  sharedArticles: Article[],
): ArticleSection[] {
  return [
    // Moon-specific sections
    {
      id: "guides",
      accentColor: EMBER.colors.warm,
      showProgress: true,
      articles: moonArticles.filter((a) => hasAnyTag(a, SECTION_TAGS.guides!)),
    },
    {
      id: "travel",
      accentColor: EMBER.colors.blue,
      showProgress: true,
      articles: moonArticles.filter((a) => hasAnyTag(a, SECTION_TAGS.travel!)),
    },
    {
      id: "decisions",
      accentColor: EMBER.colors.coral,
      showProgress: true,
      articles: moonArticles.filter((a) =>
        hasAnyTag(a, SECTION_TAGS.decisions!),
      ),
    },
    {
      id: "science",
      accentColor: EMBER.colors.teal,
      showProgress: true,
      articles: moonArticles.filter((a) => hasAnyTag(a, SECTION_TAGS.science!)),
    },
    {
      id: "costs",
      accentColor: EMBER.colors.gold,
      showProgress: true,
      articles: moonArticles.filter((a) => hasAnyTag(a, SECTION_TAGS.costs!)),
    },
    // Shared sections from Olly articles
    {
      id: "conditions",
      accentColor: EMBER.colors.coral,
      showProgress: true,
      articles: sharedArticles.filter((a) =>
        hasAnyTag(a, SECTION_TAGS.conditions!),
      ),
    },
    {
      id: "treatments",
      accentColor: EMBER.colors.teal,
      showProgress: true,
      articles: sharedArticles.filter((a) =>
        hasAnyTag(a, SECTION_TAGS.treatments!),
      ),
    },
    {
      id: "lifestyle",
      accentColor: EMBER.colors.green,
      showProgress: true,
      articles: sharedArticles.filter((a) =>
        hasAnyTag(a, SECTION_TAGS.lifestyle!),
      ),
    },
    {
      id: "support",
      accentColor: EMBER.colors.gold,
      showProgress: true,
      articles: sharedArticles.filter((a) =>
        hasAnyTag(a, SECTION_TAGS.support!),
      ),
    },
  ].filter((s) => s.articles.length > 0);
}

/* ─── Topic Filter Chips ─── */

const ALL_FILTER_CHIPS: { id: string; icon: LucideIcon }[] = [
  { id: "all", icon: Layers },
  { id: "guides", icon: BookOpen },
  { id: "travel", icon: Plane },
  { id: "decisions", icon: GitFork },
  { id: "science", icon: FlaskConical },
  { id: "costs", icon: Coins },
  { id: "conditions", icon: Stethoscope },
  { id: "treatments", icon: Pill },
  { id: "lifestyle", icon: Leaf },
  { id: "support", icon: Heart },
  { id: "encyclopedia", icon: Library },
  { id: "saved", icon: Bookmark },
];

// Only show chips for sections that have articles (plus all/encyclopedia/saved, always valid)
const ALWAYS_VISIBLE_CHIPS = new Set(["all", "encyclopedia", "saved"]);

type FilterChip = string;

/* ─── Personalization ─── */
// Match by article id (locale-invariant) rather than title — titles vary by
// active locale, so equality checks against literal English titles would fail.

function getRecommendedArticles(
  allArticles: Article[],
  treatmentGoal: string | null,
  conditions: string[] | undefined,
  isLoading: boolean,
): Article[] {
  const ids = getRecommendedArticleIds(treatmentGoal, conditions, isLoading);
  if (isLoading) return [];

  const push = (id: string) => {
    if (!ids.includes(id)) ids.push(id);
  };

  // Fill remaining slots from all articles (avoid duplicates)
  for (const a of allArticles) {
    if (ids.length >= 6) break;
    push(a.id);
  }

  return ids
    .map((id) => allArticles.find((a) => a.id === id))
    .filter((a): a is Article => !!a);
}

/* ─── Spotlight Badge ─── */

function SpotlightBadge() {
  const t = useTranslations();
  const shimmer = useSharedValue(0);

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    shimmer.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 2000 }),
        withTiming(0, { duration: 2000 }),
      ),
      -1,
      false,
    );
    return () => {
      cancelAnimation(shimmer);
    };
  }, [shimmer, reducedMotion]);

  const borderStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: interpolateColor(
      shimmer.value,
      [0, 0.5, 1],
      [
        "rgba(255, 255, 255, 0.1)",
        "rgba(255, 255, 255, 0.4)",
        "rgba(255, 255, 255, 0.1)",
      ],
    ),
  }));

  return (
    <Tag
      label={t("learn.todays_spotlight")}
      icon={Sparkles}
      tone="onDark"
      shape="pill"
      // Absolutely positioned, so it rings the tag without taking a slot.
      trailing={<Animated.View pointerEvents="none" style={borderStyle} />}
    />
  );
}

/* ─── Encyclopedia Cards (same style as Access) ─── */

const SILK_BROWN = require("../../assets/images/silk-brown.png");
const SILK_CHARCOAL = require("../../assets/images/silk-charcoal.png");
const SILK_TEAL = require("../../assets/images/silk-teal.png");
const SILK_COPPER = require("../../assets/images/silk-copper.jpg");

const TEXTURE_MAP = {
  brown: SILK_BROWN,
  charcoal: SILK_CHARCOAL,
  teal: SILK_TEAL,
  copper: SILK_COPPER,
} as const;

type TextureKey = keyof typeof TEXTURE_MAP;

type EncyclopediaShapeConfig = {
  size: number;
  radius: number;
  top: number;
  right: number;
  clipPath?: string;
  texture?: TextureKey;
};

let encyclopediaCssInjected = false;
function injectEncyclopediaShimmerCSS() {
  if (encyclopediaCssInjected || Platform.OS !== "web") return;
  encyclopediaCssInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes encyclopedia-drift {
      0%   { transform: translate(-30px, 20px); }
      25%  { transform: translate(20px, -10px); }
      50%  { transform: translate(30px, -20px); }
      75%  { transform: translate(-20px, 10px); }
      100% { transform: translate(-30px, 20px); }
    }
    @media (prefers-reduced-motion: reduce) {
      [style*="encyclopedia-drift"] { animation: none !important; }
    }
  `;
  document.head.appendChild(style);
}

function EncyclopediaTexturedShape({
  shape,
}: {
  shape: EncyclopediaShapeConfig;
}) {
  const shimmer = useRef(new RNAnimated.Value(0)).current;

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (Platform.OS === "web") {
      injectEncyclopediaShimmerCSS();
      return;
    }
    if (reducedMotion) return;
    const loop = RNAnimated.loop(
      RNAnimated.timing(shimmer, {
        toValue: 1,
        duration: 8000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer, reducedMotion]);

  const translateX = shimmer.interpolate({
    inputRange: [0, 0.25, 0.5, 0.75, 1],
    outputRange: [-30, 20, 30, -20, -30],
  });
  const translateY = shimmer.interpolate({
    inputRange: [0, 0.25, 0.5, 0.75, 1],
    outputRange: [20, -10, -20, 10, 20],
  });

  return (
    <View
      style={{
        position: "absolute",
        width: shape.size,
        height: shape.size,
        borderRadius: Platform.OS === "web" ? 0 : shape.radius,
        overflow: "hidden",
        opacity: 0.85,
        top: shape.top,
        right: shape.right,
        ...(Platform.OS === "web" && {
          clipPath: shape.clipPath,
          WebkitClipPath: shape.clipPath,
        }),
      }}
    >
      {Platform.OS === "web" ? (
        <Image
          source={TEXTURE_MAP[shape.texture ?? "copper"]}
          style={{
            width: "160%",
            height: "160%",
            position: "absolute",
            left: "-30%",
            top: "-30%",
            animation: "encyclopedia-drift 8s linear infinite",
          }}
          resizeMode="cover"
        />
      ) : (
        <RNAnimated.Image
          source={TEXTURE_MAP[shape.texture ?? "copper"]}
          style={{
            width: "160%",
            height: "160%",
            position: "absolute",
            left: "-30%",
            top: "-30%",
            transform: [{ translateX }, { translateY }],
          }}
          resizeMode="cover"
        />
      )}
    </View>
  );
}

type EncyclopediaCardData = {
  i18nKey: "medications" | "procedures" | "tests" | "conditions";
  shape: EncyclopediaShapeConfig;
  route: "Medications" | "Conditions" | "Tests" | "Procedures";
};

const ENCYCLOPEDIA_CARDS: EncyclopediaCardData[] = [
  {
    i18nKey: "medications",
    shape: {
      size: 120,
      radius: 60,
      top: -30,
      right: -30,
      clipPath: "circle(50%)",
      texture: "charcoal",
    },
    route: "Medications",
  },
  {
    i18nKey: "procedures",
    shape: {
      size: 120,
      radius: 60,
      top: -30,
      right: -30,
      clipPath: "circle(50%)",
      texture: "copper",
    },
    route: "Procedures",
  },
  {
    i18nKey: "tests",
    shape: {
      size: 120,
      radius: 60,
      top: -30,
      right: -30,
      clipPath: "circle(50%)",
      texture: "teal",
    },
    route: "Tests",
  },
  {
    i18nKey: "conditions",
    shape: {
      size: 120,
      radius: 60,
      top: -30,
      right: -30,
      clipPath: "circle(50%)",
      texture: "brown",
    },
    route: "Conditions",
  },
];

function EncyclopediaCard({
  card,
  onPress,
}: {
  card: EncyclopediaCardData;
  onPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("EncyclopediaCard");
  const titleKey = `learn.encyclopedia_cards.${card.i18nKey}_title`;
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(t(titleKey)), titleKey);
        onPress();
      }}
      style={{
        ...cardSurface(),
        flex: 1,
        minWidth: "46%",
        overflow: "hidden",
        ...(Platform.OS === "web" && {
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        }),
      }}
    >
      <EncyclopediaTexturedShape shape={card.shape} />

      {/* Inner highlight for gloss */}
      <View
        style={{
          position: "absolute",
          top: 1,
          left: 1,
          right: 1,
          height: "50%",
          borderTopLeftRadius: CARD_RADIUS - 1,
          borderTopRightRadius: CARD_RADIUS - 1,
          borderTopWidth: 1,
          borderLeftWidth: 0.5,
          borderRightWidth: 0.5,
          borderColor: semanticColors.border.glass,
        }}
      />

      {/* Content — pinned to bottom-left */}
      <View
        style={{
          padding: 18,
          paddingTop:
            card.shape.size -
            (card.shape.top ? Math.abs(card.shape.top) : 0) +
            12,
          paddingBottom: 20,
          justifyContent: "flex-end",
          minHeight: 190,
        }}
      >
        <View style={{ gap: 3 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
              letterSpacing: -0.3,
            }}
          >
            {t(`learn.encyclopedia_cards.${card.i18nKey}_title`)}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12.5,
              color: EMBER.colors.textLight,
              lineHeight: 17,
            }}
          >
            {t(`learn.encyclopedia_cards.${card.i18nKey}_subtitle`)}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textFaint,
              lineHeight: 15,
              marginTop: 2,
            }}
          >
            {t(`learn.encyclopedia_cards.${card.i18nKey}_detail`)}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

/* ─── Screen ─── */

type Props = NativeStackScreenProps<ChatStackParamList, "Learn">;

export default function LearnScreen({ navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("LearnScreen");
  const { locale } = useLocale();
  const { isRead, markAsRead } = useReadArticles();
  const { favorites } = useFavoriteArticles();
  const [activeFilter, setActiveFilter] = useState<FilterChip>("all");
  const [aiExpanded, setAiExpanded] = useState(false);
  const [chatwootVisible, setChatwootVisible] = useState(false);
  const { data: patient, isLoading: isPatientLoading } = usePatient();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const canonicalGoal =
    authorizedExperience.status === "ready"
      ? (authorizedExperience.projection?.context.familyBuildingPlan
          .treatmentGoal ?? null)
      : null;
  const clinicalConditions = useMemo(
    () =>
      (patient?.medicalHistory?.diseases ?? [])
        .map((disease) => disease.disease ?? "")
        .filter(Boolean),
    [patient?.medicalHistory?.diseases],
  );
  const isJourneyContextLoading =
    isPatientLoading || authorizedExperience.status === "loading";

  const expandProgress = useSharedValue(0);
  const scrollRef = useRef<ScrollView>(null);
  const aiCardOffset = useRef(0);

  const { moonArticles, sharedArticles, spotlightArticle, allArticles } =
    useMemo(() => collectArticles(locale, t), [locale, t]);
  const articleSections = useMemo(
    () => buildArticleSections(moonArticles, sharedArticles),
    [moonArticles, sharedArticles],
  );
  const filterChips = useMemo(() => {
    const activeIds = new Set(articleSections.map((s) => s.id));
    return ALL_FILTER_CHIPS.filter(
      (c) => ALWAYS_VISIBLE_CHIPS.has(c.id) || activeIds.has(c.id),
    );
  }, [articleSections]);

  const scrollToAiCard = useCallback(() => {
    scrollRef.current?.scrollTo({
      y: aiCardOffset.current - 20,
      animated: true,
    });
  }, []);

  const recommended = useMemo(
    () =>
      getRecommendedArticles(
        allArticles,
        canonicalGoal,
        clinicalConditions,
        isJourneyContextLoading,
      ),
    [allArticles, canonicalGoal, clinicalConditions, isJourneyContextLoading],
  );

  const toggleAiExpand = useCallback(() => {
    const expanding = !aiExpanded;
    setAiExpanded(expanding);
    expandProgress.value = withSpring(expanding ? 1 : 0, {
      damping: 28,
      stiffness: 180,
      mass: 0.8,
    });
  }, [aiExpanded, expandProgress]);

  const openArticle = useCallback(
    (article: Article, sourceSection?: string) => {
      markAsRead(article.id);
      navigation.navigate("ArticleDetail", {
        articleId: article.id,
        ...(sourceSection ? { utm_source: sourceSection } : {}),
      });
    },
    [markAsRead, navigation],
  );

  const savedArticles = useMemo(
    () => allArticles.filter((a) => favorites.includes(a.id)),
    [allArticles, favorites],
  );

  const filteredSections =
    activeFilter === "saved"
      ? savedArticles.length > 0
        ? [
            {
              id: "saved",
              accentColor: EMBER.colors.warm,
              articles: savedArticles,
            },
          ]
        : []
      : activeFilter === "all"
        ? articleSections
        : articleSections.filter((s) => s.id === activeFilter);

  return (
    <GlassScreenLayout>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader title={t("learn.title")} subtitle={t("learn.subtitle")} />

        {/* AI Assistant Card */}
        <View
          onLayout={(e) => {
            aiCardOffset.current = e.nativeEvent.layout.y;
          }}
        >
          <AiAssistantCard
            expanded={aiExpanded}
            onToggle={toggleAiExpand}
            progress={expandProgress}
            onRequestScrollTo={scrollToAiCard}
            returnTab="Learn"
          />
        </View>

        {/* Topic Filter Chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 18,
          }}
        >
          {filterChips.map(({ id, icon }) => (
            <MoonFilterChip
              key={id}
              label={t(`learn.filter_chips.${id}`)}
              icon={icon}
              selected={activeFilter === id}
              onPress={() => setActiveFilter(id)}
              tone="glass"
              showCheck={false}
            />
          ))}
        </ScrollView>

        {/* Recommended For You */}
        {activeFilter === "all" && recommended.length > 0 && (
          <View>
            <SectionHeader style={{ marginBottom: 10, paddingHorizontal: 4 }}>
              {t("learn.recommended_for_you")}
            </SectionHeader>
            <View
              style={{
                marginHorizontal: -16,
                ...(Platform.OS === "web" &&
                  ({
                    WebkitMaskImage:
                      "linear-gradient(to right, black 90%, transparent 100%)",
                    maskImage:
                      "linear-gradient(to right, black 90%, transparent 100%)",
                  } as Record<string, string>)),
              }}
            >
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{
                  gap: 12,
                  paddingLeft: 16,
                  paddingRight: 32,
                }}
              >
                {recommended.map((article, index) => {
                  return (
                    <Pressable
                      key={article.id}
                      onPress={() => {
                        // Article titles are authored catalogue copy, not
                        // patient data — safe as a label. The stable `action`
                        // keeps the funnel from splitting per article/locale.
                        capturePress(
                          pressLabel(article.title),
                          "learn_recommended_article_card",
                        );
                        openArticle(article, "recommended");
                      }}
                      style={{
                        ...cardSurface({ variant: "feature" }),
                        width: 200,
                        height: 260,
                      }}
                    >
                      <Image
                        source={
                          RECOMMENDED_TEXTURES[
                            index % RECOMMENDED_TEXTURES.length
                          ]
                        }
                        style={{
                          position: "absolute",
                          width: "120%",
                          height: "120%",
                          left: "-10%",
                          top: "-10%",
                        }}
                        resizeMode="cover"
                      />
                      <View
                        style={{
                          flex: 1,
                          padding: 18,
                          justifyContent: "space-between",
                        }}
                      >
                        <Tag
                          label={article.tag}
                          tone="onDark"
                          shape="pill"
                          style={{ alignSelf: "flex-start" }}
                        />

                        <View style={{ gap: 8 }}>
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 17,
                              fontWeight: "700",
                              color: semanticColors.onDark.text.primary,
                              lineHeight: 22,
                            }}
                          >
                            {article.title}
                          </Text>
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 12,
                              color: semanticColors.onDark.text.secondary,
                              lineHeight: 17,
                            }}
                          >
                            {article.teaser}
                          </Text>
                        </View>
                      </View>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
          </View>
        )}

        {/* Encyclopedia section */}
        {(activeFilter === "all" || activeFilter === "encyclopedia") && (
          <View style={{ marginTop: 24 }}>
            <SectionHeader style={{ paddingHorizontal: 4, marginBottom: 12 }}>
              {t("learn.encyclopedia")}
            </SectionHeader>

            <View className="flex-row flex-wrap gap-3">
              {ENCYCLOPEDIA_CARDS.map((card) => (
                <EncyclopediaCard
                  key={card.route}
                  card={card}
                  onPress={() => navigation.navigate(card.route)}
                />
              ))}
            </View>
          </View>
        )}

        {/* Empty state for Saved filter */}
        {activeFilter === "saved" && savedArticles.length === 0 && (
          <EmptyState
            testID="learn-saved-empty-state"
            density="compact"
            illustration={
              <DialogIcon
                source={savedArticlesIllustration}
                width={68}
                height={80}
                testID="learn-saved-empty-illustration"
              />
            }
            title={t("learn.no_saved_articles_yet")}
            titleVariant="featured"
            subtitle={t("learn.bookmark_articles_while_reading")}
            action={
              <CompactActionButton
                label={t("learn.browse_articles")}
                action="learn.browse_articles"
                leadingIcon={BookOpen}
                variant="primary"
                onPress={() => setActiveFilter("all")}
              />
            }
            style={{ paddingVertical: 48 }}
          />
        )}

        {/* Empty state for filters with no articles. Excludes "encyclopedia",
            which intentionally has no article sections — it renders the
            encyclopedia cards above, so an empty-articles message would be a
            spurious "No articles yet" beneath valid content. */}
        {activeFilter !== "all" &&
          activeFilter !== "saved" &&
          activeFilter !== "encyclopedia" &&
          filteredSections.length === 0 && (
            <EmptyState
              icon={BookOpen}
              iconOrb={false}
              title={t("learn.empty_articles_title")}
              subtitle={t("learn.empty_articles_subtitle")}
              style={{ paddingVertical: 48 }}
            />
          )}

        {/* Article Sections with Spotlight after first */}
        {filteredSections.map((section, index) => (
          <View key={section.id}>
            <ArticleSectionCard
              label={t(`learn.section_labels.${section.id}`)}
              articles={section.articles}
              onOpenArticle={(article) => openArticle(article, section.id)}
              isRead={isRead}
              showProgress={section.showProgress}
            />
            {index === 0 && activeFilter === "all" && spotlightArticle && (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(spotlightArticle.title),
                    "learn_spotlight_article_card",
                  );
                  openArticle(spotlightArticle, "spotlight");
                }}
                style={{
                  ...cardSurface({ variant: "feature" }),
                  height: 220,
                  marginTop: 20,
                }}
              >
                <Image
                  source={require("../../assets/images/spotlight-bg.png")}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                  }}
                  resizeMode="cover"
                />
                <View
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    borderRadius: FEATURE_CARD_RADIUS,
                    borderWidth: 0.5,
                    borderColor: semanticColors.onDark.fill.subtle,
                  }}
                />
                <View
                  style={{
                    flex: 1,
                    padding: 24,
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 14,
                  }}
                >
                  <SpotlightBadge />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 21,
                      fontWeight: "700",
                      color: semanticColors.onDark.text.primary,
                      lineHeight: 27,
                      textAlign: "center",
                      maxWidth: "90%",
                    }}
                  >
                    {spotlightArticle.title}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: semanticColors.onDark.text.secondary,
                      lineHeight: 19,
                      textAlign: "center",
                      maxWidth: "80%",
                    }}
                  >
                    {spotlightArticle.teaser}
                  </Text>
                </View>
              </Pressable>
            )}
          </View>
        ))}
      </ScrollView>
      <ChatwootSheet
        visible={chatwootVisible}
        onClose={() => setChatwootVisible(false)}
      />
    </GlassScreenLayout>
  );
}
